'use strict';

const libPictProvider = require('pict-provider');

const HOST_MOUNT = '#RecordDates-Host';

// Mode config — Project vs Contract important dates.
const MODES =
{
	Project:
	{
		Mode: 'Project', RecordEntity: 'Project', DateEntity: 'ProjectDate', DatePlural: 'ProjectDates',
		TypeDefaultPlural: 'ProjectTypeDefaultDates', RecordIDField: 'IDProject', TypeIDField: 'IDProjectType',
		DateRowIDField: 'IDProjectDate', PermEntity: 'ProjectDate', TypeDefaultTypeField: 'IDProjectType',
	},
	Contract:
	{
		Mode: 'Contract', RecordEntity: 'Contract', DateEntity: 'ContractDate', DatePlural: 'ContractDates',
		TypeDefaultPlural: 'ContractTypeDefaultDates', RecordIDField: 'IDContract', TypeIDField: 'IDContractType',
		DateRowIDField: 'IDContractDate', PermEntity: 'ContractDate', TypeDefaultTypeField: 'IDContractType',
	},
};

// Per-contract schedule basis (Calendar Day vs Working Day). Stored as a CONTRACT-scoped Configuration value
// (LevelContext 'Contract', LevelContextID = the contract id) under a fixed key. NOTE (R-Schedule): writing
// contract-scope config is permission-gated server-side (a normal contract editor currently 403s) and the payment
// engine must READ this for it to affect payments — both are being sorted out with the platform team.
const SCHEDULE_NAMESPACE = 'ContractSchedule';
const SCHEDULE_SUBKEY = 'ScheduleBasis';

/**
 * RecordDates — the bespoke "important dates" editor for a Project (ProjectDate) or Contract (ContractDate). Shows the
 * milestone set expected by the record's TYPE (ProjectTypeDefaultDate / ContractTypeDefaultDate, aligned by Hash) as
 * set-or-missing rows plus any ad-hoc dates, with a friendly date picker per row. Reachable standalone
 * (#/ProjectDates/:id, #/ContractDates/:id) and embedded as the "Important Dates" tab of the Project/Contract
 * workspaces. Contract mode also exposes the schedule basis (Calendar Day / Working Day). Writes are per-row.
 */
class RecordDatesProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._loadToken = 0;   // guards against a stale in-flight load painting a different record's dates
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID || tmpUser.IDCustomer) : 0; }

	_can(pEntity, pAction)
	{
		const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function')) { return tmpApp.EffectivePermissionsService.can(pEntity, pAction); }
		return true;
	}

	get state()
	{
		if (!this.pict.AppData.RecordDates)
		{
			this.pict.AppData.RecordDates =
			{
				Mode: 'Project', IDRecord: 0, IDType: 0, IDCustomer: 0, MountSelector: HOST_MOUNT,
				Standalone: false, Record: null, LoadedKey: '',
				Loading: false, Loaded: false, Error: '',
				Rows: [], New: { Name: '', Hash: '', Date: '' }, NewError: '',
				// Project mode also surfaces the LINKED CONTRACT's important dates (read-only) — for some project
				// types the milestone dates live on the contract, not the project, so the tab shows both.
				ContractRows: [], ContractInfo: null,
				Schedule: { Basis: '', Loaded: false, Saving: false, Notice: '' },
			};
		}
		return this.pict.AppData.RecordDates;
	}
	get cfg() { return MODES[this.state.Mode] || MODES.Project; }

	// ── REST helpers ─────────────────────────────────────────────────────────────
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_upsertEntity(pEntity, pRecord) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.upsertEntity(pEntity, pRecord, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return (tmpRC && (typeof tmpRC.deleteEntity === 'function')) ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr) => resolve(!pErr))) : Promise.resolve(false); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_isoToYMD(pISO) { const tmpV = String(pISO || ''); return (tmpV.length >= 10 && tmpV.slice(0, 10) !== '0000-00-00') ? tmpV.slice(0, 10) : ''; }
	_ymdToISO(pYMD) { const tmpV = String(pYMD || '').trim(); return tmpV ? `${tmpV}T00:00:00.000Z` : null; }

	// ── Open (standalone route or workspace embed) ───────────────────────────────
	// Standalone (its own route) shows a record-identity header; embedded (workspace tab) omits it since the
	// workspace already shows the record name + tabs around this host.
	openAsync(pMode, pID) { return this._openInternal(pMode, pID, HOST_MOUNT, true); }
	openInto(pMode, pID, pMountSelector) { return this._openInternal(pMode, pID, pMountSelector || HOST_MOUNT, false); }

	async _openInternal(pMode, pID, pMountSelector, pStandalone)
	{
		const tmpState = this.state;
		const tmpMode = (pMode === 'Contract') ? 'Contract' : 'Project';
		const tmpID = Number(pID) || 0;
		const tmpKey = `${tmpMode}:${tmpID}`;
		// Same record already loaded (e.g. the workspace repainted the host on a background render): just re-attach into
		// the current host — do NOT reset/reload. Prevents reload flicker and discarding an in-progress edit.
		if (tmpState.LoadedKey === tmpKey && tmpState.Loaded && !tmpState.Loading)
		{
			tmpState.MountSelector = pMountSelector; tmpState.Standalone = !!pStandalone;
			this.render();
			return;
		}
		tmpState.Mode = tmpMode;
		tmpState.MountSelector = pMountSelector;
		tmpState.Standalone = !!pStandalone;
		tmpState.Record = null; tmpState.LoadedKey = '';
		tmpState.IDRecord = tmpID;
		tmpState.Loading = true; tmpState.Loaded = false; tmpState.Error = '';
		tmpState.Rows = []; tmpState.New = { Name: '', Hash: '', Date: '' }; tmpState.NewError = '';
		tmpState.ContractRows = []; tmpState.ContractInfo = null;
		tmpState.Schedule = { Basis: '', Loaded: false, Saving: false, Notice: '' };
		const tmpCfg = this.cfg;
		tmpState.Can = { Create: this._can(tmpCfg.PermEntity, 'Create'), Update: this._can(tmpCfg.PermEntity, 'Update'), Delete: this._can(tmpCfg.PermEntity, 'Delete') };
		const tmpToken = ++this._loadToken;
		this.render();
		await this._loadAsync(tmpToken);
	}

	async _loadAsync(pToken)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpID = tmpState.IDRecord;
		const fStale = () => (pToken !== undefined) && (pToken !== this._loadToken);   // a newer open() superseded this load
		if (!tmpID) { tmpState.Loading = false; tmpState.Error = 'No record specified.'; this.render(); return; }
		const tmpRecord = await this._getJSON(`${tmpCfg.RecordEntity}/${tmpID}`);
		if (fStale()) { return; }
		if (!tmpRecord || !tmpRecord[tmpCfg.RecordIDField]) { tmpState.Loading = false; tmpState.Error = `${tmpCfg.RecordEntity} ${tmpID} not found.`; this.render(); return; }
		tmpState.Record = tmpRecord;
		tmpState.IDType = Number(tmpRecord[tmpCfg.TypeIDField]) || 0;
		tmpState.IDCustomer = Number(tmpRecord.IDCustomer) || this.callerCustomer();
		const tmpDefaults = tmpState.IDType
			? await this._getJSON(`${tmpCfg.TypeDefaultPlural}/FilteredTo/FBV~${tmpCfg.TypeDefaultTypeField}~EQ~${tmpState.IDType}/0/2000`)
			: [];
		if (fStale()) { return; }
		const tmpDates = await this._getJSON(`${tmpCfg.DatePlural}/FilteredTo/FBV~${tmpCfg.RecordIDField}~EQ~${tmpID}/0/2000`);
		if (fStale()) { return; }
		tmpState.Rows = this._alignRows(Array.isArray(tmpDefaults) ? tmpDefaults : [], Array.isArray(tmpDates) ? tmpDates : []);
		// Project tab ALSO shows the linked contract's important dates (read-only) — some project types carry their
		// milestone dates on the CONTRACT (ContractDate), not the project, so the tab surfaces both. (Steven 2026-10-01.)
		if (tmpState.Mode === 'Project')
		{
			const tmpIDContract = Number(tmpRecord.IDContract) || 0;
			if (tmpIDContract) { await this._loadLinkedContractDatesAsync(tmpIDContract); if (fStale()) { return; } }
		}
		if (tmpState.Mode === 'Contract') { await this._loadScheduleAsync(); if (fStale()) { return; } }
		tmpState.Loading = false; tmpState.Loaded = true;
		tmpState.LoadedKey = `${tmpState.Mode}:${tmpID}`;
		this.render();
	}

	/** Align the type's expected date set (by Hash) with the record's own date rows. pCfg lets the Project tab also
	 *  build the linked-contract rows with the Contract mode config (defaults to the active mode's config). */
	_alignRows(pDefaults, pDates, pCfg)
	{
		const tmpCfg = pCfg || this.cfg;
		const tmpDateByHash = {};
		pDates.forEach((pRow) => { const tmpH = String(pRow.Hash || '').toLowerCase(); if (tmpH) { tmpDateByHash[tmpH] = pRow; } });
		const tmpUsed = {};
		const tmpRows = [];
		// expected (type-default) rows first — in the type's order
		pDefaults.forEach((pDef) =>
		{
			const tmpHash = String(pDef.Hash || '').trim();
			const tmpHashKey = tmpHash.toLowerCase();
			const tmpMatch = tmpHashKey ? tmpDateByHash[tmpHashKey] : null;
			if (tmpMatch) { tmpUsed[Number(tmpMatch[tmpCfg.DateRowIDField])] = true; }
			tmpRows.push(this._rowFrom(pDef.Name || tmpHash, tmpHash, tmpMatch, true, tmpCfg));
		});
		// any ad-hoc record dates whose Hash is not a type default
		pDates.forEach((pRow) =>
		{
			if (tmpUsed[Number(pRow[tmpCfg.DateRowIDField])]) { return; }
			tmpRows.push(this._rowFrom(pRow.Name || pRow.Hash || '(unnamed)', pRow.Hash || '', pRow, false, tmpCfg));
		});
		return tmpRows;
	}

	_rowFrom(pName, pHash, pDateRow, pIsDefault, pCfg)
	{
		const tmpCfg = pCfg || this.cfg;
		const tmpYMD = pDateRow ? this._isoToYMD(pDateRow.Date) : '';
		return {
			Name: String(pName || ''), Hash: String(pHash || ''),
			RowID: pDateRow ? (Number(pDateRow[tmpCfg.DateRowIDField]) || 0) : 0,
			Date: tmpYMD, IsDefault: !!pIsDefault, IsSet: !!tmpYMD,
			Editing: false, Saving: false, Error: '', DeleteConfirm: false,
		};
	}

	/** Load the linked contract's important dates (Contract-mode config) for the read-only "Contract dates" section of
	 *  the Project tab. Mirrors the Contract tab's own load — type defaults aligned with the contract's own date rows. */
	async _loadLinkedContractDatesAsync(pIDContract)
	{
		const tmpState = this.state;
		const tmpCCfg = MODES.Contract;
		const tmpContract = await this._getJSON(`${tmpCCfg.RecordEntity}/${pIDContract}`);
		if (!tmpContract || !tmpContract[tmpCCfg.RecordIDField]) { return; }
		const tmpCType = Number(tmpContract[tmpCCfg.TypeIDField]) || 0;
		const tmpCDefaults = tmpCType
			? await this._getJSON(`${tmpCCfg.TypeDefaultPlural}/FilteredTo/FBV~${tmpCCfg.TypeDefaultTypeField}~EQ~${tmpCType}/0/2000`)
			: [];
		const tmpCDates = await this._getJSON(`${tmpCCfg.DatePlural}/FilteredTo/FBV~${tmpCCfg.RecordIDField}~EQ~${pIDContract}/0/2000`);
		tmpState.ContractRows = this._alignRows(Array.isArray(tmpCDefaults) ? tmpCDefaults : [], Array.isArray(tmpCDates) ? tmpCDates : [], tmpCCfg);
		tmpState.ContractInfo = { IDContract: pIDContract, Name: tmpContract.Name || `Contract #${pIDContract}` };
	}

	// ── Schedule basis (Contract) ────────────────────────────────────────────────
	async _loadScheduleAsync()
	{
		const tmpState = this.state;
		const tmpRes = await this._postJSON('Configuration/Get', { LevelContext: 'Contract', LevelContextID: tmpState.IDRecord, NamespaceKey: SCHEDULE_NAMESPACE, NamespaceValue: SCHEDULE_SUBKEY });
		const tmpBody = (tmpRes && (tmpRes.result !== undefined)) ? tmpRes.result : tmpRes;
		const tmpRaw = (tmpBody && (tmpBody.Config !== undefined)) ? tmpBody.Config : ((tmpBody && tmpBody.Document) || tmpBody);
		const tmpBasis = (tmpRaw && (tmpRaw.Basis === 'Calendar' || tmpRaw.Basis === 'Working')) ? tmpRaw.Basis : '';
		tmpState.Schedule = { Basis: tmpBasis, Loaded: true, Saving: false, Notice: '' };
	}

	async setScheduleBasis(pBasis)
	{
		const tmpState = this.state;
		if (!tmpState.Can || !tmpState.Can.Update) { return; }
		const tmpBasis = (pBasis === 'Working') ? 'Working' : 'Calendar';
		tmpState.Schedule.Basis = tmpBasis; tmpState.Schedule.Saving = true; tmpState.Schedule.Notice = '';
		this.render();
		const tmpRes = await this._postJSON(`Configuration/Put/${SCHEDULE_NAMESPACE}/${SCHEDULE_SUBKEY}/Contract/${tmpState.IDRecord}`, { Config: { Basis: tmpBasis } });
		tmpState.Schedule.Saving = false;
		tmpState.Schedule.Notice = tmpRes.error ? 'Could not save the schedule basis.' : 'Saved.';
		this.render();
	}

	// ── Row editing ──────────────────────────────────────────────────────────────
	editRow(pIndex) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow._orig = { Name: tmpRow.Name, Hash: tmpRow.Hash, Date: tmpRow.Date }; tmpRow.Editing = true; tmpRow.Error = ''; this.render(); } }
	cancelEditRow(pIndex) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { if (tmpRow._orig) { tmpRow.Name = tmpRow._orig.Name; tmpRow.Hash = tmpRow._orig.Hash; tmpRow.Date = tmpRow._orig.Date; } tmpRow.Editing = false; tmpRow.Error = ''; this.render(); } }
	setRowDate(pIndex, pValue) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow.Date = String(pValue || ''); } }
	setRowName(pIndex, pValue) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow.Name = String(pValue == null ? '' : pValue); } }
	setRowHash(pIndex, pValue) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow.Hash = String(pValue == null ? '' : pValue); } }
	setNewField(pField, pValue) { const tmpState = this.state; if (Object.prototype.hasOwnProperty.call(tmpState.New, pField)) { tmpState.New[pField] = String(pValue == null ? '' : pValue); } }

	async saveRowAsync(pIndex)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpRow = tmpState.Rows[pIndex];
		if (!tmpRow || !tmpState.Can.Update) { return; }
		const tmpName = String(tmpRow.Name || '').trim();
		if (!tmpName) { tmpRow.Error = 'Give the date a name.'; this.render(); return; }
		tmpRow.Name = tmpName;
		tmpRow.Hash = String(tmpRow.Hash || '').trim() || tmpName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
		tmpRow.Saving = true; tmpRow.Error = '';
		this.render();
		// Persist Name + Hash + Date together — the code is editable when editing the record.
		const tmpBody = { Name: tmpRow.Name, Hash: tmpRow.Hash, Date: this._ymdToISO(tmpRow.Date) };
		tmpBody[tmpCfg.DateRowIDField] = tmpRow.RowID || 0;
		if (!tmpRow.RowID) { tmpBody[tmpCfg.RecordIDField] = tmpState.IDRecord; tmpBody.IDCustomer = tmpState.IDCustomer; }
		const tmpRes = await this._upsertEntity(tmpCfg.DateEntity, tmpBody);
		if (tmpRes && tmpRes.result && tmpRes.result[tmpCfg.DateRowIDField]) { tmpRow.RowID = Number(tmpRes.result[tmpCfg.DateRowIDField]); }
		tmpRow.Saving = false;
		if (tmpRes && tmpRes.error) { tmpRow.Error = 'Save failed.'; }
		else { tmpRow.IsSet = !!tmpRow.Date; tmpRow.Editing = false; tmpRow._orig = null; }
		this.render();
	}

	/** Clear just a row's DATE (persist Date=null) while keeping the row — a one-click shortcut for Edit → clear → Save. */
	async unsetRowAsync(pIndex)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpRow = tmpState.Rows[pIndex];
		if (!tmpRow || !tmpState.Can.Update || tmpRow.Saving) { return; }
		if (!tmpRow.Date && !tmpRow.RowID) { return; }   // nothing persisted to clear
		tmpRow.Saving = true; tmpRow.Error = '';
		this.render();
		const tmpBody = { Name: tmpRow.Name, Hash: tmpRow.Hash, Date: null };
		tmpBody[tmpCfg.DateRowIDField] = tmpRow.RowID || 0;
		if (!tmpRow.RowID) { tmpBody[tmpCfg.RecordIDField] = tmpState.IDRecord; tmpBody.IDCustomer = tmpState.IDCustomer; }
		const tmpRes = await this._upsertEntity(tmpCfg.DateEntity, tmpBody);
		if (tmpRes && tmpRes.result && tmpRes.result[tmpCfg.DateRowIDField]) { tmpRow.RowID = Number(tmpRes.result[tmpCfg.DateRowIDField]); }
		tmpRow.Saving = false;
		if (tmpRes && tmpRes.error) { tmpRow.Error = 'Could not clear the date.'; }
		else { tmpRow.Date = ''; tmpRow.IsSet = false; tmpRow.Editing = false; tmpRow._orig = null; }
		this.render();
	}

	async addAdHocAsync()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (!tmpState.Can.Create) { return; }
		const tmpName = String(tmpState.New.Name || '').trim();
		if (!tmpName) { tmpState.NewError = 'Give the date a name.'; this.render(); return; }
		const tmpHash = String(tmpState.New.Hash || '').trim() || tmpName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
		tmpState.NewError = '';
		const tmpBody = { Name: tmpName, Hash: tmpHash, Date: this._ymdToISO(tmpState.New.Date), IDCustomer: tmpState.IDCustomer };
		tmpBody[tmpCfg.DateRowIDField] = 0; tmpBody[tmpCfg.RecordIDField] = tmpState.IDRecord;
		const tmpRes = await this._upsertEntity(tmpCfg.DateEntity, tmpBody);
		if (tmpRes && tmpRes.error) { tmpState.NewError = 'Could not add the date.'; this.render(); return; }
		const tmpRow = this._rowFrom(tmpName, tmpHash, tmpRes.result || null, false);
		if (!tmpRow.RowID && tmpRes.result && tmpRes.result[tmpCfg.DateRowIDField]) { tmpRow.RowID = Number(tmpRes.result[tmpCfg.DateRowIDField]); }
		tmpState.Rows.push(tmpRow);
		tmpState.New = { Name: '', Hash: '', Date: '' };
		this.render();
	}

	askDeleteRow(pIndex) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow.DeleteConfirm = true; this.render(); } }
	cancelDeleteRow(pIndex) { const tmpRow = this.state.Rows[pIndex]; if (tmpRow) { tmpRow.DeleteConfirm = false; this.render(); } }
	async deleteRowAsync(pIndex)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpRow = tmpState.Rows[pIndex];
		if (!tmpRow || tmpRow.IsDefault || !tmpState.Can.Delete) { return; }   // default rows are owned by the type, never deleted here
		if (tmpRow.RowID) { const tmpOK = await this._deleteEntity(tmpCfg.DateEntity, tmpRow.RowID); if (!tmpOK) { tmpRow.Error = 'Delete failed.'; tmpRow.DeleteConfirm = false; this.render(); return; } }
		tmpState.Rows.splice(pIndex, 1);
		this.render();
	}

	// ── Render (into the mount selector) ─────────────────────────────────────────
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpSel = this.state.MountSelector || HOST_MOUNT;
		if (tmpCA.getElement(tmpSel).length === 0) { return; }
		tmpCA.assignContent(tmpSel, this._html());
	}

	_html()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return `<div class="rd-wrap">${this._headerHTML()}<p class="rd-note">Loading…</p></div>`; }
		if (tmpState.Error) { return `<div class="rd-wrap">${this._headerHTML()}<p class="rd-error">${this._esc(tmpState.Error)}</p></div>`; }
		return `<div class="rd-wrap">${this._headerHTML()}${(tmpState.Mode === 'Contract') ? this._scheduleHTML() : ''}${this._rowsHTML()}${this._contractRowsHTML()}${this._addHTML()}</div>`;
	}

	/** Record-identity header — only on the standalone route (the workspace tab has its own chrome). */
	_headerHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Standalone) { return ''; }
		const tmpRec = tmpState.Record;
		const tmpName = this._esc((tmpRec && tmpRec.Name) || `${tmpState.Mode} #${tmpState.IDRecord}`);
		const tmpBits = tmpRec
			? ((tmpState.Mode === 'Contract')
				? [ tmpRec.PrimaryProjectNumber, tmpRec.StateProjectNumber, tmpRec.Status ]
				: [ tmpRec.Status, tmpRec.ExternalProjectID ]).filter((pBit) => pBit)
			: [];
		return `<div class="rd-head"><div class="rd-eyebrow">${this._esc(tmpState.Mode)} · Important dates</div>`
			+ `<div class="rd-title">${tmpName}</div>`
			+ (tmpBits.length ? `<div class="rd-sub">${this._esc(tmpBits.join(' · '))}</div>` : '') + `</div>`;
	}

	_scheduleHTML()
	{
		const tmpState = this.state;
		const tmpBasis = tmpState.Schedule.Basis;
		const tmpDisabled = (!tmpState.Can.Update || tmpState.Schedule.Saving) ? ' disabled' : '';
		const tmpNotice = tmpState.Schedule.Notice ? `<span class="rd-sched-notice">${this._esc(tmpState.Schedule.Notice)}</span>` : '';
		return `<div class="rd-section rd-schedule"><div class="rd-section-head">Contract schedule</div>`
			+ `<p class="rd-note">How contracted days are counted for contractor payments.</p>`
			+ `<div class="rd-sched-opts">`
			+ `<label class="rd-radio"><input type="radio" name="rd-basis"${tmpBasis === 'Calendar' ? ' checked' : ''}${tmpDisabled} onchange="_Pict.providers.RecordDates.setScheduleBasis('Calendar')"> Calendar Day</label>`
			+ `<label class="rd-radio"><input type="radio" name="rd-basis"${tmpBasis === 'Working' ? ' checked' : ''}${tmpDisabled} onchange="_Pict.providers.RecordDates.setScheduleBasis('Working')"> Working Day</label>`
			+ `${tmpNotice}</div></div>`;
	}

	_rowsHTML()
	{
		const tmpState = this.state;
		const tmpEditable = tmpState.Can.Update;
		// When the Project tab also shows the linked contract's dates below, label this section "Project dates".
		const tmpHead = (tmpState.Mode === 'Project' && tmpState.ContractInfo) ? 'Project dates' : 'Important dates';
		if (!tmpState.Rows.length)
		{
			const tmpEmpty = tmpState.ContractInfo
				? 'No dates on the project itself — the linked contract’s dates are shown below.'
				: `No dates defined for this ${this._esc(tmpState.Mode.toLowerCase())} yet.`;
			return `<div class="rd-section"><div class="rd-section-head">${tmpHead}</div><p class="rd-note">${tmpEmpty}</p></div>`;
		}
		const tmpRows = tmpState.Rows.map((pRow, pIdx) =>
		{
			const tmpChip = pRow.IsSet ? '<span class="rd-chip rd-chip-set">set</span>' : '<span class="rd-chip rd-chip-missing">not set</span>';
			const tmpKind = pRow.IsDefault ? '' : '<span class="rd-chip rd-chip-adhoc">added</span>';
			const tmpErr = pRow.Error ? `<span class="rd-row-err">${this._esc(pRow.Error)}</span>` : '';
			if (pRow.Editing)
			{
				return `<div class="rd-row rd-row-editing">`
					+ `<input class="rd-row-nameinput" type="text" value="${this._esc(pRow.Name)}" oninput="_Pict.providers.RecordDates.setRowName(${pIdx}, this.value)" placeholder="Date name">`
					+ `<input class="rd-row-code" type="text" value="${this._esc(pRow.Hash)}" oninput="_Pict.providers.RecordDates.setRowHash(${pIdx}, this.value)" placeholder="code" title="Stable code financial forms reference this date by (e.g. NTP)">`
					+ `<input class="rd-date" type="date" value="${this._esc(pRow.Date)}" onchange="_Pict.providers.RecordDates.setRowDate(${pIdx}, this.value)">`
					+ `<button class="rd-btn rd-btn-sm rd-btn-primary"${pRow.Saving ? ' disabled' : ''} onclick="_Pict.providers.RecordDates.saveRowAsync(${pIdx})">${pRow.Saving ? 'Saving…' : 'Save'}</button>`
					+ `<button class="rd-btn rd-btn-sm"${pRow.Saving ? ' disabled' : ''} onclick="_Pict.providers.RecordDates.cancelEditRow(${pIdx})">Cancel</button>${tmpErr}</div>`;
			}
			// read-only display (default) — editing is explicit via Edit
			const tmpCode = pRow.Hash ? `<span class="rd-code" title="Code referenced by financial forms">${this._esc(pRow.Hash)}</span>` : '';
			const tmpDateDisp = pRow.Date ? `<span class="rd-date-disp">${this._esc(pRow.Date)}</span>` : '<span class="rd-date-disp rd-muted">—</span>';
			const tmpEdit = tmpEditable ? `<button class="rd-btn rd-btn-sm" onclick="_Pict.providers.RecordDates.editRow(${pIdx})">Edit</button>` : '';
				// Unset clears just the DATE (back to null) and keeps the row — offered only when a date is set.
				const tmpUnset = (tmpEditable && pRow.IsSet) ? `<button class="rd-btn rd-btn-sm"${pRow.Saving ? ' disabled' : ''} title="Clear the date but keep this row" onclick="_Pict.providers.RecordDates.unsetRowAsync(${pIdx})">Unset</button>` : '';
			const tmpDel = (!pRow.IsDefault && tmpState.Can.Delete)
				? (pRow.DeleteConfirm
					? `<span class="rd-confirm">Delete? <button class="rd-btn rd-btn-sm rd-btn-danger" onclick="_Pict.providers.RecordDates.deleteRowAsync(${pIdx})">Yes</button><button class="rd-btn rd-btn-sm" onclick="_Pict.providers.RecordDates.cancelDeleteRow(${pIdx})">No</button></span>`
					: `<button class="rd-icon rd-icon-x" title="Remove" onclick="_Pict.providers.RecordDates.askDeleteRow(${pIdx})">✕</button>`)
				: '';
			return `<div class="rd-row"><div class="rd-row-name">${this._esc(pRow.Name)} ${tmpCode} ${this._sourceChip('Project')} ${tmpKind}</div>`
				+ `${tmpDateDisp}${tmpChip}${tmpEdit}${tmpUnset}${tmpDel}${tmpErr}</div>`;
		}).join('');
		return `<div class="rd-section"><div class="rd-section-head">${tmpHead}</div>${tmpRows}</div>`;
	}

	/** A "Project"/"Contract" source chip — only meaningful on the Project tab, which mixes both sources. Returns ''
	 *  on the Contract tab or a project with no linked contract (single source, so the section header already says it). */
	_sourceChip(pSource)
	{
		if (!((this.state.Mode === 'Project') && this.state.ContractInfo)) { return ''; }
		return (pSource === 'Contract')
			? '<span class="rd-chip rd-chip-src-contract" title="This date lives on the linked contract">Contract</span>'
			: '<span class="rd-chip rd-chip-src-project" title="This date lives on the project">Project</span>';
	}

	/** Read-only "Contract dates" section shown on the Project tab — the linked contract's milestone dates (they are
	 *  shared by every project on that contract, so they are edited on the contract, not here). */
	_contractRowsHTML()
	{
		const tmpState = this.state;
		if ((tmpState.Mode !== 'Project') || !tmpState.ContractInfo) { return ''; }
		const tmpLink = `#/Contract/Workspace/${Number(tmpState.ContractInfo.IDContract)}/dates`;
		const tmpHead = `<div class="rd-section-head">Contract dates <span class="rd-sub-inline">from <a href="${tmpLink}">${this._esc(tmpState.ContractInfo.Name)}</a></span></div>`;
		const tmpRows = tmpState.ContractRows || [];
		if (!tmpRows.length) { return `<div class="rd-section rd-section-contract">${tmpHead}<p class="rd-note">No dates on the linked contract.</p></div>`; }
		const tmpBody = tmpRows.map((pRow) =>
		{
			const tmpChip = pRow.IsSet ? '<span class="rd-chip rd-chip-set">set</span>' : '<span class="rd-chip rd-chip-missing">not set</span>';
			const tmpCode = pRow.Hash ? `<span class="rd-code" title="Code referenced by financial forms">${this._esc(pRow.Hash)}</span>` : '';
			const tmpDateDisp = pRow.Date ? `<span class="rd-date-disp">${this._esc(pRow.Date)}</span>` : '<span class="rd-date-disp rd-muted">—</span>';
			return `<div class="rd-row rd-row-readonly"><div class="rd-row-name">${this._esc(pRow.Name)} ${tmpCode} ${this._sourceChip('Contract')}</div>${tmpDateDisp}${tmpChip}</div>`;
		}).join('');
		return `<div class="rd-section rd-section-contract">${tmpHead}`
			+ `<p class="rd-note">These milestone dates live on the linked contract (shared by its projects). Edit them on the contract.</p>${tmpBody}</div>`;
	}

	_addHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Can.Create) { return ''; }
		const tmpErr = tmpState.NewError ? `<span class="rd-row-err">${this._esc(tmpState.NewError)}</span>` : '';
		return `<div class="rd-section rd-add"><div class="rd-section-head">Add a date</div><div class="rd-row">`
			+ `<input class="rd-new-name" type="text" placeholder="Date name" value="${this._esc(tmpState.New.Name)}" oninput="_Pict.providers.RecordDates.setNewField('Name', this.value)">`
			+ `<input class="rd-new-code" type="text" placeholder="code (optional)" title="Stable code financial forms reference this date by — e.g. NTP. Auto-derived from the name if left blank." value="${this._esc(tmpState.New.Hash)}" oninput="_Pict.providers.RecordDates.setNewField('Hash', this.value)">`
			+ `<input class="rd-date" type="date" value="${this._esc(tmpState.New.Date)}" onchange="_Pict.providers.RecordDates.setNewField('Date', this.value)">`
			+ `<button class="rd-btn rd-btn-sm rd-btn-primary" onclick="_Pict.providers.RecordDates.addAdHocAsync()">Add</button>${tmpErr}</div></div>`;
	}
}

module.exports = RecordDatesProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'RecordDates',
	AutoInitialize: true,
	AutoInitializeOrdinal: 44,
};
